import type { OnboardingStepNumber } from '@doneverse/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getNotifier } from '../api/index.js'
import { queryKeys } from '../queries/index.js'
import { onboardingService } from '../services/index.js'

/**
 * Complete one of the two founder-assertable onboarding steps (4 = product tour
 * done or skipped, 9 = doer intro video confirmed) — ClickUp 86d3uknr2.
 *
 * Invalidates the `/v1/me` query on success so the tracker moves immediately
 * rather than waiting for the transition's realtime notification to arrive.
 * That notification is still the mechanism for the steps the founder does NOT
 * trigger — 1/3/8 are closed by DocuSeal and Calendly webhooks the browser
 * never sees, and `useNotificationSubscription` invalidates the same query when
 * `onboarding_step_changed` / `dashboard_unlocked` lands.
 *
 * Scoped to the `me` key rather than the whole `profile` prefix, which would
 * also refetch the brief, workspaces, founders and loyalty queries — none of
 * which a step completion can change.
 *
 * The READ side is `selectOnboarding()` in `@doneverse/data/utils`, a pure
 * function of the profile the app shell already holds — see the note there for
 * why this deliberately isn't a query hook.
 */
export function useCompleteOnboardingStep() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ step, metadata }: { step: OnboardingStepNumber; metadata?: Record<string, unknown> }) =>
      onboardingService.completeStep(step, metadata),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.profile, 'me'] })
    },
    onError: (error) => {
      console.error('Error completing onboarding step:', error)
      getNotifier().error('Could not save that just yet. Please try again.')
    },
  })
}
